import React, { useState, useEffect, useMemo } from 'react'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import TextInput from '@jimmyboys/liita-ui/dist/components/TextInput'
import MultiSelect from '@jimmyboys/liita-ui/dist/components/MultiSelect'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import { useFetchList } from 'store'

import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'

import styles from './Dialog.module.sass'
import useSession from 'hooks/useSession'
import { translateErrorMessage } from 'hooks/useSubmitTask'
import { useReload } from 'store'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { LinkedElementType } from 'utils/enums'

const CommentDialog = (props) => {
	const { className, data, resolve, dismiss } = props

	const { i18n, expireSession, pushNotification } = useGlobal()
	const [comment, setComment] = useState('')
	const [file, setFile] = useState(null)
	const [title, setTitle] = useState(null)
	const [notify, setNotify] = useState([])

	const [template, setTemplate] = useState(null)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)
	const [linkedTo, setLinkedTo] = useState(null)

	const { user } = useSession()
	const reload = useReload()

	const isManager = user?.roles?.includes('manager')
	const isDoctor = user?.roles?.includes('doctor')
	// Removed AT and backoffice role, because backoffice is to be removed
	// const isAT = data?.causaltyBranch === 'AT'
	// const hasBackofficeRole = user?.roles?.some((role) =>
	// 	role?.startsWith('backoffice')
	// )

	const {
		state,
		contents: [
			titles = [],
			textTemplates = [],
			linksOptions = [],
			notificationRecipientsList = [],
		],
	} = useFetchList(
		[
			'/metadata/comment_titles',
			'/metadata/comment_templates',
			`/clinical_processes/${data?.processId}/element_links`,
			`/clinical_processes/${data?.processId}/comments/notification_recipients`,
		],
		[
			(json) => {
				return { value: `${json.id}`, text: i18n.translate(json.title) }
			},
			(json) => {
				return {
					value: `${json.id}`,
					text: i18n.translate(json.templateName),
					body: json.template,
				}
			},
			(json) => {
				return { value: json.id, text: json.name }
			},
			(json) => {
				return {
					value: json.id,
					text: i18n.translate(json.name),
				}
			},
		]
	)

	const isLoading = state === 'loading'

	useEffect(() => {
		console.log(notify)
	}, [notify])

	const notificationRecipients = useMemo(() => {
		const serviceProvidersToNotify = notify.filter(
			(provider) => !isNaN(Number(provider.value))
		)

		const list = notificationRecipientsList.reduce((acc, cur) => {
			const obj = {
				...cur,
				isDisabled:
					!isNaN(Number(cur.value)) &&
					serviceProvidersToNotify.length &&
					serviceProvidersToNotify.find(
						(elem) => elem.value === cur.value
					) === undefined,
			}

			acc.push(obj)

			return acc
		}, [])

		// Removed forward to backoffice, because backoffice is to be removed
		// if (!isAT && hasBackofficeRole) {
		// 	list.unshift({
		// 		value: 'trust_management',
		// 		text: i18n.translate('Trust: Management'),
		// 	})
		// }

		return list
	}, [notificationRecipientsList, notify, /*isAT, hasBackofficeRole,*/ i18n])

	useEffect(() => {
		if (titles && !title) {
			setTitle(isManager ? titles?.[1]?.value : titles?.[0]?.value)
		}
	}, [isManager, title, titles])

	useEffect(() => {
		if (template) {
			const templateText = textTemplates.find(
				({ value }) => value === template
			)
			setComment(templateText?.body || '')
		}
	}, [template, textTemplates])

	useEffect(() => {
		if (data.appointmentId && !linkedTo) {
			setLinkedTo(`appointment-${data.appointmentId}`)
		} else if (data.requisitionId && !linkedTo) {
			setLinkedTo(`requisition-${data.requisitionId}`)
		}
	}, [linksOptions, linkedTo, data.appointmentId, data.requisitionId])

	const handleSubmit = (e) => {
		e.preventDefault()
		if (submitting) {
			return
		}
		const url = `${API_URL}/api/clinical_processes/${data?.processId}/comments`
		const formData = new FormData()

		let linkedElementType = null
		let linkedElementId = null

		if (linkedTo && linkedTo.indexOf('-') !== -1) {
			linkedElementType = linkedTo.split('-')[0]
			linkedElementId = linkedTo.split('-')[1]
		}

		// NOTE: some changes were needed in this logic to match requirements from the client
		// https://trustsaude.atlassian.net/browse/CM-456?focusedCommentId=10122

		const {
			notifyInsuranceCompanyManagement,
			notifyInsuranceCompanyClinicalDirector,
			notifyTrustClinicalDirection,
			notifyTrustManagement,
			visibleToInsuranceCompany,
		} = notify.reduce(
			(result, selection) => {
				switch (selection.value) {
					case 'insurance_company_management':
						result.notifyInsuranceCompanyManagement = true
						break
					case 'insurance_company_clinical_director':
						result.notifyInsuranceCompanyClinicalDirector = true
						break
					case 'insurance_company_clinical_assistance':
						result.visibleToInsuranceCompany = true
						break
					case 'trust_clinical_direction':
						result.notifyTrustClinicalDirection = true
						break
					case 'trust_management':
						result.notifyTrustManagement = true
						break
					default:
						break
				}
				return result
			},
			{
				notifyInsuranceCompanyManagement: false,
				notifyInsuranceCompanyClinicalDirector: false,
				notifyTrustClinicalDirection: false,
				notifyTrustManagement: false,
				visibleToInsuranceCompany: false,
			}
		)

		const visibleToPublic = true
		let visibleToServiceProvider = false

		const serviceProvidersToNotify = notify
			.filter((provider) => !isNaN(Number(provider.value)))
			.map((provider) => +provider.value)

		if (serviceProvidersToNotify.length > 0) {
			formData.append('notificationRecipients', serviceProvidersToNotify)
			visibleToServiceProvider = true
		}

		if (!isDoctor) {
			formData.append('titleId', title ?? 1)
		} else {
			formData.append('titleId', 1)
		}

		formData.append('comment', comment)

		if (data?.commentId) {
			formData.append('parent', data.commentId)
		}

		formData.append(
			'notifyInsuranceCompanyManagement',
			notifyInsuranceCompanyManagement
		)
		formData.append(
			'notifyInsuranceCompanyClinicalDirector',
			notifyInsuranceCompanyClinicalDirector
		)
		formData.append(
			'notifyTrustClinicalDirection',
			notifyTrustClinicalDirection
		)
		formData.append('notifyTrustManagement', notifyTrustManagement)
		formData.append('visibleToPublic', visibleToPublic)
		formData.append('visibleToServiceProvider', visibleToServiceProvider)
		formData.append('visibleToInsuranceCompany', visibleToInsuranceCompany)

		if (linkedElementType !== null) {
			formData.append(
				'linkedElementType',
				linkedElementType === 'appointment'
					? LinkedElementType.Appointment
					: LinkedElementType.Requisition
			)
			formData.append('linkedElementId', linkedElementId)
		}

		if (file) {
			formData.append('file', file, file?.name)
		}
		setError(null)
		setSubmitting(true)

		fetch(
			url,
			fetchOptions({
				method: 'POST',
				body: formData,
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				reload([`/clinical_processes/${data?.processId}/comments`])
				resolve && resolve()
				pushNotification(
					undefined,
					'success',
					i18n`Comment inserted successfully!`
				)
			})
			.catch((err) => {
				if (err.status === '401' || err.status === 401) {
					expireSession()
				}

				const errorMessage = translateErrorMessage(err, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}
	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<form
				id={`form_notifications`}
				className={`${styles['root'] || ''} ${className || ''}`}
				onSubmit={handleSubmit}
				disabled={submitting}
			>
				<fieldset>
					<div className={styles['dialog-title']}>
						<small>{i18n`Enter your commentary`}</small>
						{data?.commentId && (
							<small>
								{' '}
								({i18n`reply to comment #${data.commentId}`})
							</small>
						)}
					</div>
					{!isDoctor ? (
						<Dropdown
							placeholder={i18n`Title`}
							value={title}
							options={titles}
							optionsAlign='right'
							onChange={(e) => {
								setTitle(e.target.value)
							}}
						/>
					) : null}
					<Dropdown
						placeholder={i18n`Predefined texts`}
						value={template}
						options={textTemplates}
						optionsAlign='right'
						onChange={(e) => {
							setTemplate(e.target.value)
						}}
					/>
					<TextInput
						placeholder={i18n`Commentary`}
						value={comment}
						multiline
						noResize
						rows='3'
						required
						onChange={(e) => {
							setTemplate(null)
							setComment(e.target.value)
						}}
					/>
				</fieldset>
				{!data?.commentId && (
					<fieldset>
						{isLoading ? (
							<LoadingIndicator
								className={`${styles['inner-loading-indicator']} ${styles['--fade']} ${styles['--inline']}`}
								label={i18n`Loading linked elements...`}
							/>
						) : (
							<Dropdown
								placeholder={i18n`Link to process element`}
								value={linkedTo}
								options={linksOptions}
								optionsAlign='right'
								onChange={(e) => {
									setLinkedTo(e.target.value)
								}}
							/>
						)}
					</fieldset>
				)}
				<fieldset>
					<label className={styles['file-chooser']}>
						<HStack
							align='center'
							justify='space-between'
							gap='xsmall'
						>
							<VStack>
								<small>
									<strong>{i18n`Attach a file`}</strong>
								</small>
								<input
									type='file'
									onChange={(e) => {
										setFile(e.target.files[0])
									}}
								/>
								<small className={styles['filename']}>
									{file?.name}
								</small>
							</VStack>
							<span className={styles['file-chooser-button']}>
								<small>{i18n`Browse`}</small>
							</span>
						</HStack>
					</label>
				</fieldset>
				{!isDoctor && (
					<fieldset>
						{isLoading ? (
							<>
								<LoadingIndicator
									className={`${styles['inner-loading-indicator']} ${styles['--fade']} ${styles['--inline']}`}
									label={i18n`Loading recipients...`}
								/>
							</>
						) : (
							<MultiSelect
								placeholder={i18n`Notifications recipients`}
								values={notify}
								options={notificationRecipients}
								onChange={(e) => {
									setNotify(e.target.value)
								}}
							/>
						)}
					</fieldset>
				)}
			</form>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={`form_notifications`} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Send`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default CommentDialog
